import React, { useState, useRef } from 'react'
import { logout } from '@multiversx/sdk-dapp/utils'
import { useGetAccount } from '@multiversx/sdk-dapp/hooks/account'
import { useAccount, useDisconnect } from 'wagmi'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Tooltip } from 'react-tooltip'

import Icon from 'src/ui/atoms/Icon'
import ButtonIcon from 'src/ui/atoms/Button/Icon'
import useOnClickOutside from 'src/hooks/useOnClickOutside'
import { formatAddressEllipsis } from 'src/util/formatAddressEllipsis'

const UserWalletsDropdown = () => {
  const [open, setOpen] = useState(false)
  const elRef = useRef<HTMLDivElement>(null)

  const { address } = useGetAccount()
  const { address: walletConnectAddress } = useAccount({})
  useOnClickOutside(elRef, () => {
    setOpen(false)
  })

  const { disconnect } = useDisconnect({})

  const handleMvxLogout = async () => {
    try {
      logout()
    } catch (error) {
      console.error(error)
    }
  }

  const handleEvmLogout = () => {
    disconnect()
  }

  interface IWalletItem {
    address: string
    type: 'evm' | 'mvx'
    logout?: () => void
  }

  const WalletItem = ({ address, logout, type }: IWalletItem) => {
    const [copied, setCopied] = useState(false)

    const onCopied = () => {
      setCopied(true)

      setTimeout(() => {
        setCopied(false)
      }, 2000)
    }

    return (
      <div className="flex items-center gap-4 border-b border-solid border-gray pb-4 shadow-lg last:border-none">
        <div className="align-center flex h-8 w-8 shrink-0 justify-center rounded-full bg-yellow text-sm text-white">
          <Icon name="wallet" size="lg" />
        </div>
        <div className="align-center flex flex-wrap whitespace-nowrap text-white">
          {type.toUpperCase()} Wallet Connected
          <CopyToClipboard text={address} onCopy={onCopied}>
            <span
              id="address-tooltip"
              className={`cursor-pointer text-xs ${
                copied ? 'text-green opacity-40' : 'text-gray'
              }`}>
              {formatAddressEllipsis(address)}
            </span>
          </CopyToClipboard>
          <Tooltip anchorSelect="#address-tooltip">
            <p>Copy to clipboard</p>
          </Tooltip>
        </div>
        <button
          id="logout"
          className="flex cursor-pointer flex-row items-center gap-2 py-2 font-spaceGrotesk text-white hover:text-red"
          onClick={logout}>
          <span className="icon-disconnect text-xl" />
        </button>
        <Tooltip anchorSelect="#logout">
          <p>Disconnect Wallet</p>
        </Tooltip>
      </div>
    )
  }

  return (
    <div className="relative">
      <ButtonIcon
        onClick={() => setOpen(!open)}
        active={!!(address || walletConnectAddress)}
        icon="wallet"
        type="alternative"
        size="S"
      />
      {open && (
        <div
          ref={elRef}
          className="absolute right-0 top-9 z-20 flex min-w-fit flex-wrap gap-4 rounded-xl bg-navy-blue px-6 py-2">
          {!walletConnectAddress && !address && (
            <div className="whitespace-nowrap text-white">You do not have any wallet connected</div>
          )}
          {walletConnectAddress ? (
            <WalletItem address={walletConnectAddress} type="evm" logout={handleEvmLogout} />
          ) : null}
          {address ? <WalletItem address={address} type="mvx" logout={handleMvxLogout} /> : null}
        </div>
      )}
    </div>
  )
}

export default UserWalletsDropdown
